import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useQuery } from 'react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  GitMerge,
  Loader2,
  Plus,
  Search,
  ShieldCheck,
  Sparkles,
  Tags,
  Trash2,
  X
} from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import PageHeader from '../../components/common/PageHeader';
import { adminAPI } from '../../services/api';

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const suggestionStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'all', label: 'All statuses' },
  { value: 'approved', label: 'Approved' },
  { value: 'merged', label: 'Merged' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'ignored', label: 'Ignored' }
];

const canonicalStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'all', label: 'All statuses' },
  { value: 'deprecated', label: 'Deprecated' },
  { value: 'merged', label: 'Merged' }
];

const fieldClass = 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 outline-none transition focus:border-slate-500 focus:ring-2 focus:ring-slate-100';
const buttonClass = 'inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50';

const extractError = (error, fallback) => error?.response?.data?.error || error?.response?.error || error?.message || fallback;

const labelize = (value = '') => String(value || '')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bHr\b/g, 'HR')
  .replace(/\bKyc\b/g, 'KYC');

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(date);
};

const getSuggestionName = (suggestion) => suggestion?.display_name || suggestion?.displayName || suggestion?.raw_name || suggestion?.rawName || 'Untitled suggestion';
const getSimilarTags = (suggestion) => suggestion?.similar_tags || suggestion?.similarTags || [];
const getCollisionData = (suggestion) => {
  const data = suggestion?.collision_data || suggestion?.collisionData || {};
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  return data;
};
const getOccurrences = (suggestion) => {
  const collisionData = getCollisionData(suggestion);
  return Array.isArray(collisionData.occurrences) ? collisionData.occurrences : [];
};

const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'pending') return 'border-amber-200 bg-amber-50 text-amber-700';
  if (normalized === 'approved' || normalized === 'merged' || normalized === 'active') return 'border-emerald-200 bg-emerald-50 text-emerald-700';
  if (normalized === 'rejected') return 'border-rose-200 bg-rose-50 text-rose-700';
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

const confidenceTone = (value) => {
  const score = Number(value || 0);
  if (score >= 90) return 'text-emerald-700';
  if (score >= 70) return 'text-amber-700';
  return 'text-slate-600';
};

const Chip = ({ children, className = '' }) => (
  <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium ${className}`}>
    {children}
  </span>
);

const MetricTile = ({ label, value, detail }) => (
  <article className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex items-start justify-between mb-6">
      <span className="text-sm font-semibold text-slate-600">{label}</span>
    </div>
    <div>
      <p className="text-3xl font-bold tracking-tight text-slate-900">{value}</p>
      {detail && <p className="mt-2 text-xs font-medium text-slate-400">{detail}</p>}
    </div>
  </article>
);

const Modal = ({ title, subtitle, children, footer, onClose, width = 'max-w-5xl' }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 py-6 backdrop-blur-sm"
    onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}
  >
    <section role="dialog" aria-modal="true" aria-label={title} className={`max-h-[92vh] w-full ${width} overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm`}>
      <header className="flex items-start justify-between gap-4 border-b border-slate-200 p-5">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm leading-6 text-slate-500">{subtitle}</p>}
        </div>
        <button type="button" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-900 hover:bg-slate-50" aria-label="Close modal">
          <X size={17} />
        </button>
      </header>
      <div className="max-h-[calc(92vh-152px)] overflow-y-auto p-5">{children}</div>
      {footer && <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 p-4 sm:flex-row sm:justify-end">{footer}</footer>}
    </section>
  </div>
);

const PageSkeleton = () => (
  <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8" aria-label="Loading taxonomy governance">
    <div className="mb-6 space-y-3">
      <SkeletonBlock className="h-4 w-48 rounded-md" />
      <SkeletonBlock className="h-10 w-80 rounded-lg" />
      <SkeletonBlock className="h-4 w-[46rem] max-w-full rounded-md" />
    </div>
    <div className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-24 rounded-[24px]" />)}
    </div>
    <SkeletonBlock className="mb-4 h-16 rounded-[24px]" />
    <SkeletonBlock className="h-[34rem] rounded-[24px]" />
  </div>
);

const normalizePagination = (pagination, page, limit) => {
  const total = Number(pagination?.total || 0);
  return {
    page,
    limit,
    total,
    totalPages: Math.max(1, Math.ceil(total / limit)),
    hasMore: Boolean(pagination?.hasMore)
  };
};

const PaginationControls = ({ pagination, onPageChange, onLimitChange }) => {
  const page = Number(pagination?.page || 1);
  const limit = Number(pagination?.limit || PAGE_SIZE_OPTIONS[0]);
  const total = Number(pagination?.total || 0);
  const totalPages = Math.max(1, Number(pagination?.totalPages || 1));
  const start = total ? ((page - 1) * limit) + 1 : 0;
  const end = total ? Math.min(page * limit, total) : 0;

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
      <div>
        Showing <span className="font-semibold text-slate-900">{start}-{end}</span> of <span className="font-semibold text-slate-900">{formatNumber(total)}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={limit}
          onChange={(event) => onLimitChange(Number(event.target.value))}
          className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-100"
        >
          {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} rows</option>)}
        </select>
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <span className="px-2 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
          {page} / {totalPages}
        </span>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex h-9 items-center rounded-lg border border-slate-200 bg-white px-3 font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
};

const SimilarTagCard = ({ tag, onMerge, compact = false }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-3">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-slate-900">{tag.displayName || tag.name}</p>
        <p className="mt-1 text-xs text-slate-500">{labelize(tag.categoryType || tag.categoryName)}{tag.matchReason ? ` • ${tag.matchReason}` : ''}</p>
      </div>
      {tag.similarScore !== null && tag.similarScore !== undefined && (
        <span className="rounded-md bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600">
          {(Number(tag.similarScore) * 100).toFixed(0)}%
        </span>
      )}
    </div>
    {!compact && Array.isArray(tag.aliases) && tag.aliases.length > 0 && (
      <div className="mt-2 flex flex-wrap gap-1">
        {tag.aliases.slice(0, 3).map((alias) => (
          <Chip key={alias.id || alias.aliasSlug || alias.aliasDisplay} className="border-slate-200 bg-slate-50 text-slate-600">
            {alias.aliasDisplay}
          </Chip>
        ))}
        {tag.aliases.length > 3 && <Chip className="border-slate-200 bg-slate-50 text-slate-500">+{tag.aliases.length - 3}</Chip>}
      </div>
    )}
    {onMerge && (
      <button
        type="button"
        onClick={() => onMerge(tag)}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
      >
        <GitMerge size={14} />
        Merge as alias
      </button>
    )}
  </div>
);

const TagDetailModal = ({ tag, onClose, onSaved }) => {
  const [aliasDisplay, setAliasDisplay] = useState('');
  const [saving, setSaving] = useState(false);
  const [removingAliasId, setRemovingAliasId] = useState(null);

  const detailQuery = useQuery(
    ['canonical-tag-detail', tag?.id],
    () => adminAPI.getCanonicalTag(tag.id),
    {
      enabled: Boolean(tag?.id),
      staleTime: 10000
    }
  );

  const currentTag = detailQuery.data?.tag || tag;
  const aliases = Array.isArray(currentTag?.aliases) ? currentTag.aliases : [];
  const activeAliases = aliases.filter((alias) => alias.isActive !== false);
  const inactiveAliases = aliases.filter((alias) => alias.isActive === false);

  const addAlias = async (event) => {
    event.preventDefault();
    const value = aliasDisplay.trim();
    if (!value || saving) return;
    setSaving(true);
    try {
      await adminAPI.addCanonicalTagAlias(currentTag.id, { aliasDisplay: value });
      toast.success('Alias added.');
      setAliasDisplay('');
      await detailQuery.refetch();
      onSaved();
    } catch (error) {
      toast.error(extractError(error, 'Alias could not be added.'));
    } finally {
      setSaving(false);
    }
  };

  const deactivateAlias = async (alias) => {
    if (!alias?.id || removingAliasId) return;
    const confirmed = window.confirm(`Deactivate alias "${alias.aliasDisplay}"?`);
    if (!confirmed) return;
    setRemovingAliasId(alias.id);
    try {
      await adminAPI.deactivateCanonicalTagAlias(currentTag.id, alias.id);
      toast.success('Alias deactivated.');
      await detailQuery.refetch();
      onSaved();
    } catch (error) {
      toast.error(extractError(error, 'Alias could not be deactivated.'));
    } finally {
      setRemovingAliasId(null);
    }
  };

  return (
    <Modal
      title={currentTag?.displayName || currentTag?.name || 'Canonical tag'}
      subtitle={`${labelize(currentTag?.categoryType || currentTag?.categoryName)} • ${activeAliases.length} active aliases`}
      onClose={onClose}
      width="max-w-4xl"
    >
      {detailQuery.isLoading ? (
        <div className="space-y-3">
          <SkeletonBlock className="h-20 rounded-lg" />
          <SkeletonBlock className="h-44 rounded-lg" />
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricTile label="Usage" value={formatNumber(currentTag?.usageCount)} detail={currentTag?.isVerified ? 'Verified canonical tag' : 'Not marked verified'} />
            <MetricTile label="Aliases" value={formatNumber(activeAliases.length)} detail={`${inactiveAliases.length} inactive aliases`} />
            <MetricTile label="Status" value={labelize(currentTag?.status)} detail={`Review: ${labelize(currentTag?.reviewStatus)}`} />
          </div>

          <form onSubmit={addAlias} className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
            <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
              <label>
                <span className="mb-2 block text-sm font-medium text-slate-700">Add alias</span>
                <input
                  value={aliasDisplay}
                  onChange={(event) => setAliasDisplay(event.target.value)}
                  className={fieldClass}
                  placeholder="Example: PMP, HR Manager, Staff Nurse"
                />
              </label>
              <button
                type="submit"
                disabled={saving || !aliasDisplay.trim()}
                className={`${buttonClass} bg-slate-950 text-white hover:bg-slate-800`}
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Add alias
              </button>
            </div>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              The backend blocks aliases that already map to another active canonical tag in this same category.
            </p>
          </form>

          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900">Aliases</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[760px] divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Alias</th>
                    <th className="px-6 py-4">Normalized</th>
                    <th className="px-6 py-4">Source</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {aliases.map((alias) => (
                    <tr key={alias.id || alias.aliasSlug} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium text-slate-900">{alias.aliasDisplay}</td>
                      <td className="px-6 py-4 font-mono text-xs text-slate-500">{alias.aliasSlug}</td>
                      <td className="px-6 py-4 text-slate-600">{labelize(alias.source)}</td>
                      <td className="px-6 py-4">
                        <Chip className={alias.isActive === false ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}>
                          {alias.isActive === false ? 'Inactive' : 'Active'}
                        </Chip>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {alias.isActive !== false && (
                          <button
                            type="button"
                            onClick={() => deactivateAlias(alias)}
                            disabled={removingAliasId === alias.id}
                            className="inline-flex items-center justify-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {removingAliasId === alias.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!aliases.length && (
                    <tr>
                      <td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-500">No aliases recorded for this tag.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </Modal>
  );
};

const ReviewSuggestionModal = ({ suggestion, selectedTag, categories, onClose, onSaved }) => {
  const [targetTag, setTargetTag] = useState(selectedTag || null);
  const [targetSearch, setTargetSearch] = useState(selectedTag?.displayName || getSuggestionName(suggestion));
  const [approveName, setApproveName] = useState(getSuggestionName(suggestion));
  const [notes, setNotes] = useState('');
  const [savingAction, setSavingAction] = useState(null);

  const suggestionName = getSuggestionName(suggestion);
  const categoryValue = suggestion?.category_type || suggestion?.categoryType || '';
  const similarTags = getSimilarTags(suggestion);
  const occurrences = getOccurrences(suggestion);

  const targetQuery = useQuery(
    ['taxonomy-review-target-search', targetSearch, categoryValue],
    () => adminAPI.getCanonicalTags({
      search: targetSearch.trim(),
      category: categoryValue || undefined,
      status: 'active',
      reviewStatus: 'approved',
      limit: 12,
      offset: 0
    }),
    {
      enabled: targetSearch.trim().length >= 2,
      staleTime: 15000
    }
  );

  const targetResults = targetQuery.data?.tags || [];

  const submitReview = async (action) => {
    if (savingAction) return;
    if (action === 'merge' && !targetTag?.id) {
      toast.error('Choose an existing canonical tag to merge into.');
      return;
    }
    if (action === 'approve' && !approveName.trim()) {
      toast.error('Enter the canonical display name.');
      return;
    }

    setSavingAction(action);
    try {
      const payload = {
        action,
        notes: notes.trim() || undefined
      };
      if (action === 'merge') payload.targetTagId = targetTag.id;
      if (action === 'approve') payload.displayName = approveName.trim();
      await adminAPI.reviewSuggestedTag(suggestion.id, payload);
      toast.success(action === 'merge' ? 'Suggestion merged as alias.' : `Suggestion ${action}.`);
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(extractError(error, 'Suggested tag review failed.'));
    } finally {
      setSavingAction(null);
    }
  };

  return (
    <Modal
      title={`Review "${suggestionName}"`}
      subtitle={`${labelize(categoryValue || suggestion?.category_name)} suggestion • confidence ${Number(suggestion?.confidence_score || 0).toFixed(0)}%`}
      onClose={onClose}
      width="max-w-5xl"
      footer={(
        <>
          <button type="button" onClick={onClose} className={`${buttonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submitReview('ignore')}
            disabled={Boolean(savingAction)}
            className={`${buttonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
          >
            {savingAction === 'ignore' && <Loader2 size={16} className="animate-spin" />}
            Ignore
          </button>
          <button
            type="button"
            onClick={() => submitReview('reject')}
            disabled={Boolean(savingAction)}
            className={`${buttonClass} border border-rose-200 bg-white text-rose-700 hover:bg-rose-50`}
          >
            {savingAction === 'reject' && <Loader2 size={16} className="animate-spin" />}
            Reject
          </button>
          <button
            type="button"
            onClick={() => submitReview('approve')}
            disabled={Boolean(savingAction) || !approveName.trim()}
            className={`${buttonClass} border border-slate-200 bg-white text-slate-800 hover:bg-slate-50`}
          >
            {savingAction === 'approve' && <Loader2 size={16} className="animate-spin" />}
            Approve new tag
          </button>
          <button
            type="button"
            onClick={() => submitReview('merge')}
            disabled={Boolean(savingAction) || !targetTag?.id}
            className={`${buttonClass} bg-slate-950 text-white hover:bg-slate-800`}
          >
            {savingAction === 'merge' ? <Loader2 size={16} className="animate-spin" /> : <GitMerge size={16} />}
            Merge into selected
          </button>
        </>
      )}
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          <section className="rounded-[24px] border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Chip className={statusTone(suggestion?.status)}>{labelize(suggestion?.status)}</Chip>
              <Chip className="border-slate-200 bg-slate-50 text-slate-600">{labelize(categoryValue || suggestion?.category_name)}</Chip>
              <Chip className="border-slate-200 bg-slate-50 text-slate-600">{occurrences.length || getCollisionData(suggestion).occurrenceCount || 1} occurrence(s)</Chip>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">
              Merging keeps the canonical taxonomy clean and records this suggestion as an alias of the selected existing tag. Approving creates a new canonical tag.
            </p>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="text-sm font-semibold text-slate-900">Available similar tags</h3>
              <span className="text-xs font-medium text-slate-500">{similarTags.length} suggested by backend</span>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {similarTags.map((tag) => (
                <SimilarTagCard
                  key={tag.id}
                  tag={tag}
                  onMerge={(nextTag) => {
                    setTargetTag(nextTag);
                  }}
                />
              ))}
              {!similarTags.length && (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500 md:col-span-2">
                  No close canonical tag was found automatically. Use search below before approving a new tag.
                </div>
              )}
            </div>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Search canonical tags manually</h3>
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_180px]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  value={targetSearch}
                  onChange={(event) => setTargetSearch(event.target.value)}
                  className={`${fieldClass} pl-10`}
                  placeholder="Search existing canonical tags and aliases"
                />
              </label>
              <select
                value={categoryValue}
                disabled
                className={`${fieldClass} bg-slate-50 text-slate-500`}
              >
                <option value={categoryValue}>{labelize(categoryValue || 'Category')}</option>
              </select>
            </div>
            <div className="mt-4 space-y-2">
              {targetQuery.isFetching && <p className="text-sm text-slate-500">Searching canonical tags...</p>}
              {targetResults.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => {
                    setTargetTag(tag);
                  }}
                  className={`flex w-full items-start justify-between gap-3 rounded-lg border px-3 py-3 text-left transition hover:bg-slate-50 ${
                    targetTag?.id === tag.id ? 'border-slate-950 ring-1 ring-slate-950' : 'border-slate-200'
                  }`}
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900">{tag.displayName || tag.name}</span>
                    <span className="mt-1 block text-xs text-slate-500">{labelize(tag.categoryType)} • {tag.aliasCount || 0} aliases • {tag.matchReason || 'canonical match'}</span>
                  </span>
                  {targetTag?.id === tag.id && <CheckCircle2 className="shrink-0 text-emerald-600" size={18} />}
                </button>
              ))}
              {!targetQuery.isFetching && targetSearch.trim().length >= 2 && !targetResults.length && (
                <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">No canonical tags found for this search.</p>
              )}
            </div>
          </section>
        </div>

        <aside className="space-y-4">
          <section className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
            <h3 className="text-sm font-semibold text-slate-900">Selected merge target</h3>
            {targetTag ? (
              <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                <p className="font-semibold text-slate-900">{targetTag.displayName || targetTag.name}</p>
                <p className="mt-1 text-xs text-slate-500">{labelize(targetTag.categoryType)} • {targetTag.aliasCount || 0} aliases</p>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  The suggestion text will be stored as an alias for this canonical tag.
                </p>
              </div>
            ) : (
              <p className="mt-2 text-sm leading-6 text-slate-500">No target selected. Choose a similar tag or search manually.</p>
            )}
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Approve as new tag</h3>
            <label className="mt-3 block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Canonical display name</span>
              <input
                value={approveName}
                onChange={(event) => setApproveName(event.target.value)}
                className={fieldClass}
              />
            </label>
            <p className="mt-2 text-xs leading-5 text-slate-500">
              Use only when no available tag or alias represents this meaning.
            </p>
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Review notes</h3>
            <textarea
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="mt-3 min-h-[120px] w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100"
              placeholder="Optional note for audit history"
            />
          </section>

          <section className="rounded-[24px] border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-900">Category context</h3>
            <div className="mt-3 max-h-44 overflow-y-auto pr-1">
              {categories.slice(0, 12).map((category) => (
                <div key={category.id} className={`mb-2 flex items-center justify-between rounded-lg border px-3 py-2 text-xs ${
                  category.categoryType === categoryValue ? 'border-slate-950 bg-slate-50 text-slate-900' : 'border-slate-200 text-slate-600'
                }`}>
                  <span>{category.name}</span>
                  <span>{formatNumber(category.tagCount)}</span>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </Modal>
  );
};

function TaxonomyPage() {
  const [activeTab, setActiveTab] = useState('suggestions');
  const [canonicalDraftSearch, setCanonicalDraftSearch] = useState('');
  const [canonicalSearch, setCanonicalSearch] = useState('');
  const [canonicalCategory, setCanonicalCategory] = useState('all');
  const [canonicalStatus, setCanonicalStatus] = useState('active');
  const [canonicalPage, setCanonicalPage] = useState(1);
  const [canonicalLimit, setCanonicalLimit] = useState(50);
  const [suggestionDraftSearch, setSuggestionDraftSearch] = useState('');
  const [suggestionSearch, setSuggestionSearch] = useState('');
  const [suggestionStatus, setSuggestionStatus] = useState('pending');
  const [suggestionCategory, setSuggestionCategory] = useState('all');
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [suggestionLimit, setSuggestionLimit] = useState(25);
  const [reviewModal, setReviewModal] = useState(null);
  const [tagModal, setTagModal] = useState(null);
  const [autoPromoting, setAutoPromoting] = useState(false);

  const overviewQuery = useQuery('taxonomy-overview', adminAPI.getTaxonomyOverview, {
    staleTime: 30000,
    refetchOnWindowFocus: false
  });

  const categories = useMemo(() => overviewQuery.data?.categories || [], [overviewQuery.data?.categories]);
  const categoryOptions = useMemo(
    () => categories.filter((category) => Number(category.tagCount || 0) > 0 || Number(category.aliasCount || 0) > 0),
    [categories]
  );

  const canonicalQuery = useQuery(
    ['canonical-tags', canonicalSearch, canonicalCategory, canonicalStatus, canonicalPage, canonicalLimit],
    () => adminAPI.getCanonicalTags({
      search: canonicalSearch,
      category: canonicalCategory === 'all' ? undefined : canonicalCategory,
      status: canonicalStatus,
      reviewStatus: 'approved',
      limit: canonicalLimit,
      offset: (canonicalPage - 1) * canonicalLimit
    }),
    {
      staleTime: 20000,
      refetchOnWindowFocus: false
    }
  );

  const suggestedQuery = useQuery(
    ['suggested-tags', suggestionSearch, suggestionStatus, suggestionCategory, suggestionPage, suggestionLimit],
    () => adminAPI.getSuggestedTags({
      search: suggestionSearch,
      status: suggestionStatus,
      category: suggestionCategory === 'all' ? undefined : suggestionCategory,
      limit: suggestionLimit,
      offset: (suggestionPage - 1) * suggestionLimit,
      includeSimilar: true
    }),
    {
      staleTime: 15000,
      refetchOnWindowFocus: false
    }
  );

  const summary = overviewQuery.data?.summary || {};
  const canonicalTags = canonicalQuery.data?.tags || [];
  const suggestions = suggestedQuery.data?.suggestions || [];
  const canonicalPagination = normalizePagination(canonicalQuery.data?.pagination, canonicalPage, canonicalLimit);
  const suggestionPagination = normalizePagination(suggestedQuery.data?.pagination, suggestionPage, suggestionLimit);

  const refreshTaxonomy = async () => {
    await Promise.all([
      overviewQuery.refetch(),
      canonicalQuery.refetch(),
      suggestedQuery.refetch()
    ]);
  };

  const runAutoPromotionDryRun = async () => {
    if (autoPromoting) return;
    setAutoPromoting(true);
    try {
      const result = await adminAPI.autoPromoteSuggestedTags({ dryRun: true, limit: 50, minConfidence: 92, minOccurrences: 2 });
      toast.success(`Dry run inspected ${result.inspected || 0}: ${result.approved?.length || 0} approvable, ${result.merged?.length || 0} mergeable.`);
    } catch (error) {
      toast.error(extractError(error, 'Auto-promotion dry run failed.'));
    } finally {
      setAutoPromoting(false);
    }
  };

  const applyCanonicalFilters = () => {
    setCanonicalPage(1);
    setCanonicalSearch(canonicalDraftSearch.trim());
  };

  const applySuggestionFilters = () => {
    setSuggestionPage(1);
    setSuggestionSearch(suggestionDraftSearch.trim());
  };

  if (overviewQuery.isLoading && !overviewQuery.data) return <PageSkeleton />;

  return (
    <div className="min-h-full px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Tag Taxonomy"
        description="Search approved canonical tags, review AI-suggested tags against similar existing tags, and manage aliases so duplicate meanings do not enter matching."
        actions={(
          <button
            type="button"
            onClick={runAutoPromotionDryRun}
            disabled={autoPromoting}
            className={`${buttonClass} border border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}
          >
            {autoPromoting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            Auto-promote dry run
          </button>
        )}
      />

      <section className="mb-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Canonical Tags" value={formatNumber(summary.activeApprovedTags)} detail={`${formatNumber(summary.totalTags)} total taxonomy tags`} />
        <MetricTile label="Active Aliases" value={formatNumber(summary.activeAliases)} detail="Alternative names that resolve to canonical tags" />
        <MetricTile label="Pending Suggestions" value={formatNumber(summary.pendingSuggestions)} detail="Needs admin review or merge" />
        <MetricTile label="Categories" value={formatNumber(categories.length)} detail={`${formatNumber(categoryOptions.length)} categories with live coverage`} />
      </section>

      <section className="mb-5 rounded-[24px] border border-slate-200 bg-white p-2 shadow-sm">
        <div className="grid gap-2 md:grid-cols-2">
          {[
            { key: 'suggestions', label: 'Suggested Tags', icon: Sparkles, detail: 'Review, merge, approve, reject, or ignore AI-suggested taxonomy additions.' },
            { key: 'canonical', label: 'Canonical Tags', icon: Tags, detail: 'Search approved tags and manage aliases across the expanded taxonomy.' }
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-lg border p-4 text-left transition ${
                  active ? 'border-slate-950 bg-slate-950 text-white shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} />
                  <span className="font-semibold">{tab.label}</span>
                </div>
                <p className={`mt-2 text-sm leading-5 ${active ? 'text-slate-200' : 'text-slate-500'}`}>{tab.detail}</p>
              </button>
            );
          })}
        </div>
      </section>

      {activeTab === 'suggestions' ? (
        <>
          <section className="mb-5 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_180px_220px_150px_auto]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  value={suggestionDraftSearch}
                  onChange={(event) => setSuggestionDraftSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') applySuggestionFilters();
                  }}
                  className={`${fieldClass} pl-10`}
                  placeholder="Search suggested tags"
                />
              </label>
              <select
                value={suggestionStatus}
                onChange={(event) => {
                  setSuggestionPage(1);
                  setSuggestionStatus(event.target.value);
                }}
                className={fieldClass}
              >
                {suggestionStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select
                value={suggestionCategory}
                onChange={(event) => {
                  setSuggestionPage(1);
                  setSuggestionCategory(event.target.value);
                }}
                className={fieldClass}
              >
                <option value="all">All categories</option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.categoryType}>{category.name}</option>
                ))}
              </select>
              <select
                value={suggestionLimit}
                onChange={(event) => {
                  setSuggestionPage(1);
                  setSuggestionLimit(Number(event.target.value));
                }}
                className={fieldClass}
              >
                {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} rows</option>)}
              </select>
              <button
                type="button"
                onClick={applySuggestionFilters}
                className={`${buttonClass} bg-slate-950 text-white hover:bg-slate-800`}
              >
                Apply filters
              </button>
            </div>
          </section>

          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Suggested Tags</h2>
                <p className="mt-1 text-sm text-slate-500">{formatNumber(suggestionPagination.total)} suggestions match the current filters</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Chip className="border-slate-200 bg-slate-50 text-slate-600">Similar tags shown inline</Chip>
                <Chip className="border-slate-200 bg-slate-50 text-slate-600">Merge creates alias</Chip>
              </div>
            </div>

            {suggestedQuery.isFetching && (
              <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold text-slate-700">
                Updating suggested tags...
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-[1220px] divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Suggestion</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Available Similar Tags</th>
                    <th className="px-6 py-4">Evidence</th>
                    <th className="px-6 py-4">Updated</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {suggestions.map((suggestion) => {
                    const similarTags = getSimilarTags(suggestion);
                    const occurrences = getOccurrences(suggestion);
                    const collisionData = getCollisionData(suggestion);
                    return (
                      <tr key={suggestion.id} className="align-top hover:bg-slate-50">
                        <td className="px-6 py-4">
                          <p className="max-w-[280px] font-semibold text-slate-900">{getSuggestionName(suggestion)}</p>
                          <p className="mt-1 font-mono text-xs text-slate-500">{suggestion.normalized_slug}</p>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            <Chip className={statusTone(suggestion.status)}>{labelize(suggestion.status)}</Chip>
                            <Chip className={`border-slate-200 bg-white ${confidenceTone(suggestion.confidence_score)}`}>
                              {Number(suggestion.confidence_score || 0).toFixed(0)}% confidence
                            </Chip>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-medium text-slate-800">{labelize(suggestion.category_name || suggestion.category_type)}</p>
                          <p className="mt-1 text-xs text-slate-500">{labelize(suggestion.category_type)}</p>
                        </td>
                        <td className="px-6 py-4">
                          <div className="grid max-w-[470px] gap-2">
                            {similarTags.slice(0, 3).map((tag) => (
                              <SimilarTagCard
                                key={tag.id}
                                tag={tag}
                                compact
                                onMerge={(selected) => setReviewModal({ suggestion, selectedTag: selected })}
                              />
                            ))}
                            {!similarTags.length && (
                              <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                                No similar canonical tag found. Review manually before approving.
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="font-semibold text-slate-900">{occurrences.length || collisionData.occurrenceCount || 1}</p>
                          <p className="mt-1 text-xs text-slate-500">occurrence(s)</p>
                          {Number(collisionData.collisionCount || 0) > 0 && (
                            <Chip className="mt-2 border-amber-200 bg-amber-50 text-amber-700">
                              {collisionData.collisionCount} exact collision(s)
                            </Chip>
                          )}
                        </td>
                        <td className="px-6 py-4 text-slate-600">{formatDateTime(suggestion.updated_at || suggestion.updatedAt)}</td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => setReviewModal({ suggestion, selectedTag: similarTags[0] || null })}
                            className={`${buttonClass} bg-slate-950 text-white hover:bg-slate-800`}
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {!suggestions.length && (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center">
                        <Clock3 className="mx-auto text-slate-300" size={36} />
                        <h3 className="mt-3 text-sm font-semibold text-slate-900">No suggested tags found</h3>
                        <p className="mt-1 text-sm text-slate-500">Adjust the filters or search term.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationControls
              pagination={suggestionPagination}
              onPageChange={(nextPage) => setSuggestionPage(Math.min(Math.max(1, nextPage), suggestionPagination.totalPages))}
              onLimitChange={(nextLimit) => {
                setSuggestionPage(1);
                setSuggestionLimit(nextLimit);
              }}
            />
          </section>
        </>
      ) : (
        <>
          <section className="mb-5 rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
            <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_220px_170px_150px_auto]">
              <label className="relative block">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  value={canonicalDraftSearch}
                  onChange={(event) => setCanonicalDraftSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') applyCanonicalFilters();
                  }}
                  className={`${fieldClass} pl-10`}
                  placeholder="Search canonical tags, aliases, descriptions, or slugs"
                />
              </label>
              <select
                value={canonicalCategory}
                onChange={(event) => {
                  setCanonicalPage(1);
                  setCanonicalCategory(event.target.value);
                }}
                className={fieldClass}
              >
                <option value="all">All categories</option>
                {categoryOptions.map((category) => (
                  <option key={category.id} value={category.categoryType}>{category.name}</option>
                ))}
              </select>
              <select
                value={canonicalStatus}
                onChange={(event) => {
                  setCanonicalPage(1);
                  setCanonicalStatus(event.target.value);
                }}
                className={fieldClass}
              >
                {canonicalStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
              <select
                value={canonicalLimit}
                onChange={(event) => {
                  setCanonicalPage(1);
                  setCanonicalLimit(Number(event.target.value));
                }}
                className={fieldClass}
              >
                {PAGE_SIZE_OPTIONS.map((option) => <option key={option} value={option}>{option} rows</option>)}
              </select>
              <button
                type="button"
                onClick={applyCanonicalFilters}
                className={`${buttonClass} bg-slate-950 text-white hover:bg-slate-800`}
              >
                Apply filters
              </button>
            </div>
          </section>

          <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Canonical Tags</h2>
                <p className="mt-1 text-sm text-slate-500">{formatNumber(canonicalPagination.total)} canonical tags match the current filters</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Chip className="border-emerald-200 bg-emerald-50 text-emerald-700">
                  <ShieldCheck size={13} className="mr-1" />
                  Approved taxonomy
                </Chip>
                <Chip className="border-slate-200 bg-slate-50 text-slate-600">Alias-aware search</Chip>
              </div>
            </div>

            {canonicalQuery.isFetching && (
              <div className="border-b border-slate-100 px-4 py-2 text-xs font-semibold text-slate-700">
                Updating canonical tags...
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-[1120px] divide-y divide-slate-100 text-left text-sm">
                <thead className="bg-slate-50/50 text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <tr>
                    <th className="px-6 py-4">Canonical Tag</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Aliases</th>
                    <th className="px-6 py-4">Quality</th>
                    <th className="px-6 py-4">Updated</th>
                    <th className="px-6 py-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {canonicalTags.map((tag) => (
                    <tr key={tag.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <p className="max-w-[320px] font-semibold text-slate-900">{tag.displayName || tag.name}</p>
                        <p className="mt-1 font-mono text-xs text-slate-500">{tag.coreName || tag.slug}</p>
                        {tag.matchReason && <p className="mt-2 text-xs text-cyan-700">{tag.matchReason}</p>}
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-medium text-slate-800">{labelize(tag.categoryName || tag.categoryType)}</p>
                        <p className="mt-1 text-xs text-slate-500">{labelize(tag.categoryType)}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex max-w-[360px] flex-wrap gap-1.5">
                          {(tag.aliases || []).slice(0, 5).map((alias) => (
                            <Chip key={alias.id || alias.aliasSlug || alias.aliasDisplay} className="border-slate-200 bg-slate-50 text-slate-600">
                              {alias.aliasDisplay}
                            </Chip>
                          ))}
                          {Number(tag.aliasCount || 0) > 5 && (
                            <Chip className="border-slate-200 bg-white text-slate-500">+{Number(tag.aliasCount || 0) - 5}</Chip>
                          )}
                          {!Number(tag.aliasCount || 0) && (
                            <span className="text-sm text-slate-400">No active aliases</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Chip className={statusTone(tag.status)}>{labelize(tag.status)}</Chip>
                        <p className="mt-2 text-xs text-slate-500">{tag.isVerified ? 'Verified' : 'Not verified'} • usage {formatNumber(tag.usageCount)}</p>
                      </td>
                      <td className="px-6 py-4 text-slate-600">{formatDateTime(tag.updatedAt)}</td>
                      <td className="px-6 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => setTagModal(tag)}
                          className={`${buttonClass} bg-slate-950 text-white hover:bg-slate-800`}
                        >
                          Manage aliases
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!canonicalTags.length && (
                    <tr>
                      <td colSpan={6} className="px-4 py-16 text-center">
                        <AlertTriangle className="mx-auto text-slate-300" size={36} />
                        <h3 className="mt-3 text-sm font-semibold text-slate-900">No canonical tags found</h3>
                        <p className="mt-1 text-sm text-slate-500">Try a different search term or category.</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <PaginationControls
              pagination={canonicalPagination}
              onPageChange={(nextPage) => setCanonicalPage(Math.min(Math.max(1, nextPage), canonicalPagination.totalPages))}
              onLimitChange={(nextLimit) => {
                setCanonicalPage(1);
                setCanonicalLimit(nextLimit);
              }}
            />
          </section>
        </>
      )}

      {reviewModal && (
        <ReviewSuggestionModal
          suggestion={reviewModal.suggestion}
          selectedTag={reviewModal.selectedTag}
          categories={categories}
          onClose={() => setReviewModal(null)}
          onSaved={refreshTaxonomy}
        />
      )}

      {tagModal && (
        <TagDetailModal
          tag={tagModal}
          onClose={() => setTagModal(null)}
          onSaved={refreshTaxonomy}
        />
      )}
    </div>
  );
}

export default TaxonomyPage;
